// @flow
import React, { PureComponent, Fragment } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import classNames from 'classnames';
import { RESET_POSTCODE } from '../../actions/actionTypes';
import { Bling as GPT } from 'react-gpt';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import FilterForm from 'Common/FilterForm';
import getFilterBreakdownProp from 'Common/getFilterBreakdownProp';
import { getKey } from 'Common/formValues';
import getQueryString from 'Common/getQueryString';
import buildUrlFromForm from 'Common/buildUrlFromForm';
import {
  gtmBasicOptions,
  gtmFurtherOptions,
  gtmWinners,
  gtmPageview
} from 'Common/gtmPayloads';
import {
  slots,
  createAdUnitPath,
  targetingHome,
  adUnitPageNames
} from 'Common/ads';
import scrollToTopOfPage from 'Common/scrollToTopOfPage';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import { gtmEventPush } from 'utils/gtm/events';
import getClassifiedsSite from 'utils/getClassifiedsSite';
import { setAutocarHeaderVisibility } from 'utils/autocar';

import * as Actions from 'actionTypes';
import metadata from 'App/variables/seo';
import pageData from 'utils/pageData';

import { Grid, Row, Col } from 'Common/Grid';
import Accordion from 'Common/Accordion';
import BrowseAll from 'Common/BrowseAll';
import SEO from 'Common/Seo';
import ErrorBox from 'Common/ErrorBox';
import SearchForm from 'Common//SearchForm';
import CardsCarousel from 'Common/CardsCarousel';

import styles from 'rootContainer/Home.scss';

const pageType = 'used-cars';
const thisPageData = pageData[pageType];
const homeText = thisPageData.homeText;

const createActiveFields = R.compose(
  R.keys,
  R.filter(R.identity),
  R.omit(['lat', 'long', 'nearlynew', 'used', 'isPostCodeValid', 'sortBy']),
  R.ifElse(R.prop('isPostCodeValid'), R.identity, R.dissoc('withinDistance'))
);

/**
 * <Home /> component.
 * @param {string} postcode - postcode
 * @return {class} returns home instance
 */
export class Home extends PureComponent<*, *> {
  currentBounce: Function;
  debounce: Function;
  interval: any;

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      showFurtherOptions: false,
      delayAdMasthead: true,
      delayAdOverlay: true,
      delayAdOverlay2: true
    };
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object): Object {
    /**
     * pluckFromState
     * @param {string} prop - prop
     * @param {string} key - key
     * @return {array} - options
     */
    const pluckFromState = (prop, key) =>
      getFilterBreakdownProp(state, prop, key);

    const additonalOptions = {
      fuelTypes: pluckFromState('fuelType'),
      category: pluckFromState('category'),
      colours: pluckFromState('colour'),
      doors: pluckFromState('doors'),
      seats: pluckFromState('no_of_seats'),
      transmissions: pluckFromState('transmission'),
      mileages: pluckFromState('mileage'),
      annualTax: pluckFromState('annualTax'),
      fuelConsumption: pluckFromState('ec_combined_mpg'),
      co2Emissions: pluckFromState('co2_gkm'),
      acceleration: pluckFromState('0_to_62_mph_secs'),
      insuranceGroup: pluckFromState('insuranceGroup'),
      yearFrom: pluckFromState('yearFrom'),
      yearTo: pluckFromState('yearTo'),
      engineSizeFrom: pluckFromState('badge_engine_ccFrom'),
      engineSizeTo: pluckFromState('badge_engine_ccTo')
    };

    return {
      makes: pluckFromState('make'),
      models: pluckFromState('model'),
      priceFrom: pluckFromState('cashPriceStat', 'min'),
      priceTo: pluckFromState('cashPriceStat', 'max'),
      additonalOptions,
      form: state.form.search,
      filterForm: state.form.search,
      ui: state.ui,
      results: state.results,
      isResultsLoading: state.results.isLoading,
      isUILoading: state.ui.isLoading,
      isError: state.ui.error,
      popularCars: state.popular.results,
      isMobile: state.viewport.isXs
    };
  }

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillMount it fetches the makes.
   * @return {void}
   */
  UNSAFE_componentWillMount() {
    if (!this.props.filterForm) {
      this.props.dispatch({
        type: Actions.FETCH_MAKES
      });
    }

    const year = new Date().getFullYear() - 3;

    this.props.dispatch({
      type: Actions.FETCH_POPULAR_RESULTS,
      query:
        `pageSize=30` +
        `&maxOdometerReadingMiles=30000` +
        `&minManufacturedYear=${year}`
    });

    this.postCodeCheck(this.props.ui.lastPostCode);
  }

  componentDidMount() {
    GPT.enableSingleRequest();
    targetingHome();
    this.interval = setInterval(this.pushPv, 300);
    if (this.props.form && this.props.form.values) {
      this.resetAllFieldsButKeepPostcode({ ...this.props.form.values });
    }

    // Delay loading of the top ad to prioritize side ad
    this.delayAdMastheadTimeout = setTimeout(() => {
      this.setState({ delayAdMasthead: false });
    }, 100); // Adjust delay as needed
    this.delayAdOverlayTimeout = setTimeout(() => {
      this.setState({ delayAdOverlay: false });
    }, 150); // Adjust delay as needed
    this.delayAdOverlay2Timeout = setTimeout(() => {
      this.setState({ delayAdOverlay2: false });
    }, 150); // Adjust delay as needed
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push({ page: '' });
    }

    // Clear timeout to avoid memory leaks
    clearTimeout(this.delayAdMastheadTimeout);
    clearTimeout(this.delayAdOverlayTimeout);
    clearTimeout(this.delayAdOverlay2Timeout);
  }

  pushPv: () => void = () => {
    if (isWindow() && isWindowProp('dataLayer') && isWindowProp('location')) {
      const pageViewData = {
        content_type: 'Used cars for sale',
        navigation_section: 'Used cars for sale'
      };
      window.dataLayer.push(gtmPageview(pageViewData));
      clearInterval(this.interval);
    }
  };

  submit: () => void = () => {
    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      // on mobile, puts back the scroll after submiting the form from advanced filters
      document.body.style.overflow = 'auto';
      setAutocarHeaderVisibility(true);
    }
    if (isWindow() && isWindowProp('dataLayer')) {
      if (this.state.showFurtherOptions) {
        window.dataLayer.push(gtmFurtherOptions(this.props.form.values));
      } else {
        window.dataLayer.push(gtmBasicOptions(this.props.form.values));
      }
    }
    const url = buildUrlFromForm(this.props.form, '', thisPageData.name);
    this.props.history.push(url);
  };

  pushGtm: (tag: string, title: string) => void = (tag, title) => {
    if (isWindow() && isWindowProp('dataLayer')) {
      const titleAndYear = title.split('(');
      window.dataLayer.push(
        gtmWinners(tag, titleAndYear[0], titleAndYear[1].slice(0, -1))
      );
    }
  };

  postCodeCheck: (postcode: String) => void = postcode => {
    this.props.dispatch({
      type: Actions.CHECK_POSTCODE,
      postcode
    });
    this.debounce = null;
  };

  debouncedFunc: (
    fn: (() => void) | ((postcode: String) => void)
  ) => any = fn => {
    if (!this.debounce) {
      this.debounce = debounce(fn, 1000);
    }
    return this.debounce;
  };

  checkPostcode: () => void = value => {
    let postcode;
    if (value) {
      if (!value.target.value) {
        this.resetField('postCode');
        return;
      } else {
        postcode = value.target.value;
      }
    } else {
      postcode =
        this.props.form.values && this.props.form.values.postCode
          ? this.props.form.values.postCode
          : null;
    }

    if (postcode) {
      this.currentBounce = this.debouncedFunc(this.postCodeCheck);
      this.currentBounce(postcode);
    }
  };

  isPostcodeValid: () => boolean = () => {
    const postcode =
      this.props.form &&
      this.props.form.values &&
      this.props.form.values.postCode
        ? this.props.form.values.postCode
        : '';

    if (
      !postcode ||
      (this.props.ui.lastPostCode && this.props.ui.lastPostCode !== postcode)
    ) {
      return false;
    } else if (
      this.props.form &&
      this.props.form.values &&
      (this.props.form.values.isPostCodeValid === false ||
        this.props.form.values.isPostCodeValid === true)
    ) {
      return this.props.form.values.isPostCodeValid;
    } else {
      return false;
    }
  };

  getResults: () => void = () => {
    if (this.props.form.values) {
      if (!this.props.form.values.postCode) {
        delete this.props.form.values.sortBy;
      }
    }

    this.currentBounce = this.debouncedFunc(() => {
      const query = getQueryString(this.props.form);
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query
      });
      this.debounce = null;
    });
    this.currentBounce();
  };

  toggleFurtherOptions: () => void = () => {
    const isFilterFormOpen = this.state.showFurtherOptions;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: !isFilterFormOpen ? 'More options' : 'Less options'
      }
    });
    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      document.body.style.overflow = isFilterFormOpen ? 'auto' : 'hidden';
      if (isFilterFormOpen) {
        setAutocarHeaderVisibility(true);
      } else {
        setAutocarHeaderVisibility(false);
      }
    }
    this.setState(
      {
        showFurtherOptions: !isFilterFormOpen
      },
      () => (!isFilterFormOpen ? scrollToTopOfPage() : null)
    );
  };

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField: (field: string) => void = field => {
    if (field === 'nearlynew' || field === 'used') {
      this.props.dispatch(change('search', field, true));
    } else {
      this.props.dispatch(change('search', field, null));
    }

    if (field === 'make') {
      this.props.dispatch(change('search', 'model', null));
    }

    if (field === 'postCode') {
      this.props.dispatch({ type: RESET_POSTCODE });
      this.props.dispatch(change('search', 'withinDistance', null));

      const fields = this.props.form ? { ...this.props.form.values } : {};
      if (fields.sortBy && getKey(fields.sortBy) === 'distance') {
        this.props.dispatch(change('search', 'sortBy', null));
      }
    }
  };

  removeActiveFilter: (field: string) => void = key => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Remove Active Filter'
      }
    });
    this.resetField(key);
  };

  resetAllFields(fields: any) {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Clear all'
      }
    });
    R.keys(fields).forEach(field => {
      this.resetField(field);
    });
    if (fields) {
      this.getResults();
    }
  }

  resetAllFieldsButKeepPostcode(fields: any) {
    R.keys(fields).forEach(field => {
      if (field !== 'postCode') {
        this.resetField(field);
      }
    });
    this.getResults();
  }

  /**
   * Renders the Home component.
   *
   * @return {JSX} - rendered Home page.
   */
  render(): Element<*> {
    const {
      ui,
      makes,
      models,
      results,
      form,
      priceFrom,
      priceTo,
      additonalOptions,
      isResultsLoading,
      isUILoading,
      isError,
      popularCars,
      isMobile
    } = this.props;

    const resultsLength =
      results && results.pagination ? results.pagination.total : 0;
    const fields = form ? { ...form.values } : {};
    const activeFilters = form ? createActiveFields(form.values) : [];
    // Persist postcode
    if (ui && ui.lastPostCode) {
      fields['postCode'] = ui.lastPostCode;
    }

    const postcodeVisited =
      form &&
      form.fields &&
      form.fields.postCode &&
      form.fields.postCode.visited;

    const adProps = {
      page: adUnitPageNames['home']
    };

    const carouselItemsToDisplay = { xs: 1, sm: 2, md: 2, lg: 3 };
    const classifiedsSite = getClassifiedsSite();
    return (
      <Fragment>
        {typeof window !== 'undefined' &&
          window.document &&
          (isMobile && this.state.showFurtherOptions ? (
            <Fragment>
              <div
                className={styles.filterFormBackdrop}
                onClick={this.toggleFurtherOptions}
              ></div>
              <div
                className={classNames(
                  styles.filterForm,
                  fields && styles.isVisible
                )}
              >
                <FilterForm
                  initialValues={fields}
                  onSubmit={this.submit}
                  getResults={this.getResults}
                  fields={fields}
                  activeFilters={activeFilters || []}
                  makes={makes}
                  models={models}
                  priceFrom={priceFrom}
                  priceTo={priceTo}
                  checkPostcode={this.checkPostcode}
                  isPostcodeValid={this.isPostcodeValid()}
                  additonalOptions={additonalOptions}
                  fetchUpdatedQuery={() => {}}
                  resultsLength={resultsLength}
                  isMobile={isMobile}
                  removeActiveFilter={this.removeActiveFilter}
                  resetAllFields={() => this.resetAllFields(fields)}
                  resetField={this.resetField}
                  isListingsPage={false}
                  closeFilters={this.toggleFurtherOptions}
                  isResultsLoading={isResultsLoading}
                  postcodeVisited={postcodeVisited}
                />
              </div>
            </Fragment>
          ) : (
            <SearchForm
              onSubmit={this.submit}
              getResults={this.getResults}
              checkPostcode={this.checkPostcode}
              fields={fields}
              makes={makes}
              models={models}
              additonalOptions={additonalOptions}
              resultsLength={resultsLength}
              toggleFurtherOptions={this.toggleFurtherOptions}
              showFurtherOptions={this.state && this.state.showFurtherOptions}
              isPostcodeValid={this.isPostcodeValid()}
              priceFrom={priceFrom}
              priceTo={priceTo}
              isResultsLoading={isResultsLoading}
              isUILoading={isUILoading}
              isMobile={isMobile}
              resetAllFields={() => this.resetAllFields(fields)}
              postcodeVisited={postcodeVisited}
              thisPageData={thisPageData}
            />
          ))}

        <Grid>
          <SEO {...metadata.home(pageType)} />
          {isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <ErrorBox type={500} />
              </Col>
            </Row>
          )}
          {!isError && !isMobile && !this.state.delayAdMasthead && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div className={styles.headerAd}>
                  <GPT
                    id={slots.header.id}
                    adUnitPath={createAdUnitPath(adProps)}
                    collapseEmptyDiv={true}
                    renderWhenViewable={false}
                    sizeMapping={slots.header.sizeMapping}
                    targeting={slots.header.targeting}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!isError && isMobile && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div
                  className={classNames(styles.headerAd, styles.headerAdSticky)}
                >
                  <GPT
                    id={slots.banner.id}
                    adUnitPath={createAdUnitPath(adProps)}
                    collapseEmptyDiv={true}
                    renderWhenViewable={false}
                    sizeMapping={slots.banner.sizeMapping}
                    targeting={slots.banner.targeting}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={9} colLg={9}>
                <section className={styles.intro}>
                  <h3 className={styles.heading}>
                    {homeText[classifiedsSite]['title']}
                  </h3>
                  <p className={styles.p}>
                    {homeText[classifiedsSite]['description']}
                  </p>
                </section>
                {!isError && isMobile && (
                  <div className={styles.sideAd}>
                    <GPT
                      id={slots.topMpu.id}
                      adUnitPath={createAdUnitPath(adProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.topMpu.sizeMapping}
                      targeting={slots.topMpu.targeting}
                    />
                  </div>
                )}
                {!isError && popularCars && (
                  <CardsCarousel
                    carouselItemsToDisplay={carouselItemsToDisplay}
                    extendStyle={styles.classDetailCarouselItems}
                    title={'Popular cars'}
                    history={this.props.history}
                    related={isMobile ? popularCars.slice(0, 10) : popularCars}
                    gtmSectionName={'popularCars'}
                    dataBi="autotrader-click"
                    dataSection="popular-cars"
                    utm="USEDCARSCAROUSELLINKS"
                  />
                )}
              </Col>
              <Col colXs={12} colSm={12} colMd={3} colLg={3}>
                <div className={styles.sideAd}>
                  {!isError && !isMobile && (
                    <GPT
                      id={slots.flexible.id}
                      adUnitPath={createAdUnitPath(adProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.flexible.sizeMapping}
                      targeting={slots.flexible.targeting}
                    />
                  )}
                  {!isError && isMobile && (
                    <React.Fragment>
                      {!this.state.delayAdOverlay2 && (
                        <GPT
                          id={slots.overlay2Mobile.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlay2Mobile.sizeMapping}
                          targeting={slots.overlay2Mobile.targeting}
                        />
                      )}
                    </React.Fragment>
                  )}
                  {!isError && !isMobile && (
                    <React.Fragment>
                      {!this.state.delayAdOverlay2 && (
                        <GPT
                          id={slots.overlay2.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlay2.sizeMapping}
                          targeting={slots.overlay2.targeting}
                        />
                      )}
                    </React.Fragment>
                  )}
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row>
              <Col colXs={12} className={styles.mtl}>
                <Accordion
                  accordionHeight={200}
                  gtmSectionName={'accordionBrowse'}
                >
                  <BrowseAll
                    items={makes}
                    text="makes"
                    history={this.props.history}
                    thisPageData={thisPageData}
                  />
                </Accordion>
              </Col>
            </Row>
          )}
        </Grid>
      </Fragment>
    );
  }
}

// $FlowFixMe
export default connect(Home.mapStateToProps)(Home);



// WEBPACK FOOTER //
// ./src/usedCars/Home/index.js